import * as Sentry from '@sentry/nextjs';
import { useEffect, useMemo, useState } from 'react';

import { useCurrentMediaType } from './responsive';
import { Category, GameV1 } from '@/api/games';
import { sliderParams } from '@/helpers/games';
import { getSliderParams } from '@/helpers/getSliderParams';
import { useCommonStore } from '@/store';

type FetchGamesByCategoryParams = {
  category: Category;
  pageSize?: number;
  staticAmount?: boolean;
  subName?: string;
};

export const useGameList = () => {
  const currentMediaType = useCurrentMediaType();
  const getGamesByCategory = useCommonStore.use.getGamesByCategory();

  const { allGamesPageSize } = useMemo(() => {
    const { allGames: allGamesPageSize } = getSliderParams(
      sliderParams,
      currentMediaType
    );

    return {
      allGamesPageSize,
    };
  }, [currentMediaType]);

  const [exclusiveGamesList, setExclusiveGamesList] = useState<GameV1[]>([]);
  const [exclusiveGamesLength, setExclusiveGamesLength] = useState<number>(0);

  useEffect(() => {
    const fetchGamesByCategory = ({
      category,
      pageSize,
      staticAmount = false,
      subName,
    }: FetchGamesByCategoryParams) => {
      return getGamesByCategory(category, {
        pageSize: pageSize ?? allGamesPageSize,
        isTopic: true,
        subName,
        staticAmount,
      });
    };

    const fetchGames = async () => {
      try {
        const [exclusiveGamesInfo] = await Promise.all([
          fetchGamesByCategory({
            category: Category.EXCLUSIVE,
            staticAmount: true,
          }),
        ]);

        setExclusiveGamesList(exclusiveGamesInfo?.games ?? []);

        setExclusiveGamesLength(exclusiveGamesInfo?.pagination.totalCount ?? 0);
      } catch (error: unknown) {
        Sentry.withScope((scope) => {
          scope.setTags({
            category: 'games-fetch',
            pathToFile: 'src/hooks/gameLobbbyList.ts',
          });
          scope.setLevel('error');

          Sentry.captureMessage(`Games fetch error: ${JSON.stringify(error)}`);
        });
      }
    };

    fetchGames();
  }, [getGamesByCategory, allGamesPageSize]);

  return {
    exclusiveGamesList,
    exclusiveGamesLength,
  };
};
